import {
  Children,
  cloneElement,
  FC,
  Fragment,
  isValidElement,
  useRef,
  useState
} from 'react';
import { useClickAway } from 'react-use';
import { DropDownItemType } from './dropDownItemType';

export interface DropDownOptions {
  children?: JSX.Element;
  additionalChildProps?: any;
  dropDownItems: DropDownItemType[];
  dropDirection?: 'left' | 'right';
  dropdownYPositionClass?: string;
  disablePositionRelative?: boolean;
  selectDropDownItem: (key: string) => void;
}

const DropDownButtonLegacy: FC<DropDownOptions> = ({
  children,
  additionalChildProps,
  dropDownItems,
  dropDirection,
  dropdownYPositionClass,
  disablePositionRelative: disableRelative,
  selectDropDownItem
}): JSX.Element => {
  const ref = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useClickAway(ref, () => setIsDropdownOpen(false));

  const childrenWithAdditionalChildProps = Children.map(children, (child) => {
    // Checking isValidElement is the safe way and avoids a
    // typescript error too.
    if (isValidElement(child) && additionalChildProps) {
      return cloneElement(child, { ...additionalChildProps });
    }
    return child;
  });

  const relativeClass = disableRelative ? '' : 'relative';

  return (
    <>
      <div className={`${relativeClass} block`} ref={ref}>
        <button
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          {childrenWithAdditionalChildProps}
        </button>

        {isDropdownOpen ? (
          <div
            className={`absolute z-40 min-w-fit md:right-0 w-full drop-shadow-xl rounded ${
              dropDirection === 'left' ? 'md:translate-x-0' : ''
            } ${dropdownYPositionClass}`}
            id="dropdown-menu"
            role="menu"
          >
            <div className="z-40 mt-1 bg-white min-w-fit max-w-xs box-content flex flex-col border rounded border-neutral-200">
              {dropDownItems.map(({ label, key, isActive }, index) => {
                return (
                  <Fragment key={`${key}-dropdown-item-${index}`}>
                    <a
                      className={`px-3 m-0 p-1 cursor-pointer border-0 first:rounded-t last:rounded-b transition-all whitespace-nowrap ${
                        isActive
                          ? 'bg-neutral-100 text-neutral-500'
                          : 'bg-white hover:bg-neutral-200'
                      }`}
                      onClick={() => {
                        selectDropDownItem(key);
                        setIsDropdownOpen(false);
                      }}
                    >
                      {label}
                    </a>
                  </Fragment>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default DropDownButtonLegacy;
